import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import styles from "./styles.module.scss";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  overlayCls?: string;
  backdropCls?: string;
  modalCls?: string;
  headerCls?: string;
  contentCls?: string;
  animationDuration?: number;
  manageFocus?: boolean;
  ariaLabel?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  overlayCls,
  backdropCls,
  modalCls,
  contentCls,
  animationDuration = 200,
  manageFocus = false,
  ariaLabel,
}) => {
  const [mounted, setMounted] = useState(isOpen);
  const modalRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else {
      const id = window.setTimeout(() => setMounted(false), animationDuration);
      return () => window.clearTimeout(id);
    }
  }, [isOpen, animationDuration]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        if (manageFocus) {
          event.preventDefault();
          event.stopPropagation();
        }
        onCloseRef.current();
      } else if (manageFocus && event.key === "Tab") {
        const dialog = modalRef.current;
        if (!dialog) return;
        const controls = Array.from(
          dialog.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], textarea:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'
          )
        ).filter((element) => element.getClientRects().length > 0);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (
          !first ||
          !dialog.contains(document.activeElement) ||
          document.activeElement === dialog ||
          (event.shiftKey && document.activeElement === first) ||
          (!event.shiftKey && document.activeElement === last)
        ) {
          event.preventDefault();
          event.stopPropagation();
          (event.shiftKey ? last : first)?.focus();
          if (!first) dialog.focus();
        }
      }
    };
    // An opt-in nested dialog must handle Escape before the underlying chat,
    // even if another control temporarily retains focus while it opens.
    document.addEventListener("keydown", onKey, manageFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey, manageFocus);
    };
  }, [isOpen, manageFocus]);

  useEffect(() => {
    if (!isOpen || !mounted || !manageFocus || !modalRef.current) return;
    const previousFocus = document.activeElement;
    modalRef.current.focus({ preventScroll: true });
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus({ preventScroll: true });
    };
  }, [isOpen, mounted, manageFocus]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      // Closing on mousedown can unmount the dialog before the browser's
      // default focus change. Do not let that override restored trigger focus.
      if (manageFocus) e.preventDefault();
      onClose();
    }
  };

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <div
      className={cn(styles.overlay, overlayCls, isOpen && styles.overlayOpen)}
      style={{ "--anim-time": `${animationDuration}ms` } as any}
      onMouseDown={onMouseDown}
    >
      <div
        className={cn(
          styles.backdrop,
          backdropCls,
          isOpen && styles.backdropOpen
        )}
      />
      <div
        ref={modalRef}
        role={manageFocus ? "dialog" : undefined}
        aria-modal={manageFocus && isOpen ? true : undefined}
        aria-label={ariaLabel}
        tabIndex={manageFocus ? -1 : undefined}
        className={cn(styles.modal, modalCls, isOpen && styles.modalOpen)}
      >
        <div className={cn(styles.content, contentCls)}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
